import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  getUserPurchaseHref,
  userPurchaseRow,
  userPurchasesColumns,
} from "./tableParams/purchasesColumns";
import { GET_USER_PURCHASES } from "../../../actions/ActionTypes";
import { fetchApi } from "../../../actions/FetchActions";
import { getElementsByIndex } from "../../../utils/data/Content";
import DataTable from "../../dataTable/DataTable";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";

import type { JSX } from "react";

import type { UserPurchaseRow } from "./tableParams/purchasesColumns.types";
import type { OfferListItem } from "../../../api/OfferAPI.types";
import type { AppState } from "../../../reducers";

interface PurchasesTableProps {
  userUuid: string;
  offerAbility?: boolean;
}

function PurchasesTable({
  userUuid,
  offerAbility = true,
}: PurchasesTableProps): JSX.Element {
  const { t } = useTranslation("editor-analytics");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const dispatch = useDispatch();

  const token = useSelector((state: AppState) => state.loginReducer.token);
  const offers = useSelector((state: AppState) => state.fetchReducer.offers);
  const purchases = useSelector(
    (state: AppState) => state.fetchReducer.user_purchases.data,
  );
  const isFetching = useSelector(
    (state: AppState) => state.fetchReducer.user_purchases.isFetching,
  );
  const isFetched = useSelector(
    (state: AppState) => state.fetchReducer.user_purchases.isFetched,
  );
  const failed = useSelector(
    (state: AppState) => state.fetchReducer.user_purchases.failed,
  );

  // Le reducer est partagé : avant notre requête, il porte l'état du dernier utilisateur ouvert.
  const [hasRequested, setHasRequested] = useState(false);

  const fetchPurchases = useCallback((): void => {
    if (!projectSlug) return;

    setHasRequested(true);
    dispatch(
      fetchApi({
        endpoint: `/api/editor_stats/v1/admin/user/purchases?user_uuid=${userUuid}&period_start=1970-01-01`,
        type: GET_USER_PURCHASES,
        item: "user_purchases",
        headers: [{ key: "xx-ok-project", value: projectSlug }],
        token,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- le jeton ne relance pas la requête
  }, [dispatch, projectSlug, userUuid]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  const rowList = useMemo((): UserPurchaseRow[] => {
    if (!hasRequested || failed) return [];

    return purchases.map((purchase) => {
      const offer: OfferListItem | undefined = getElementsByIndex(
        purchase.offer_uuid,
        offers,
        "uuid",
      ).at(0);

      return userPurchaseRow(purchase, offer?.label);
    });
  }, [purchases, offers, failed, hasRequested]);

  const columnList = useMemo(
    () => userPurchasesColumns({ t, projectSlug, offerAbility }),
    [t, projectSlug, offerAbility],
  );

  const getRowHref = useCallback(
    (row: UserPurchaseRow): string | undefined =>
      getUserPurchaseHref(row, projectSlug, offerAbility),
    [projectSlug, offerAbility],
  );

  const isFailed = hasRequested && Boolean(failed);

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{t("purchasesTitle")}</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columnList}
          data={rowList}
          defaultSorting={{ id: "purchase_date", desc: true }}
          emptyText={t("noDatas")}
          getRowHref={getRowHref}
          isError={isFailed}
          isLoading={!hasRequested || !isFetched || isFetching}
          onRetry={fetchPurchases}
          urlSync={{ prefix: "purchases" }}
        />
      </CardContent>
    </Card>
  );
}

export default PurchasesTable;
